/**
 * Created by Andrey Popov on 4/4/25.
 */

const ScrollTable = function (dataSource, options) {
    cleverapps.EventEmitter.call(this);

    options = options || {};

    this.scrollDisabled = options.scrollDisabled === undefined ? false : options.scrollDisabled;

    this.dataSource = dataSource;
    this.rows = this.dataSource.getData().map((rowData) => this.createRow(rowData));
    this._sort();

    this.direction = options.direction !== undefined ? options.direction : cleverapps.UI.VERTICAL;

    this._lastRemovedIndex = -1;

    this.dataSource.on("add", this.addRow.bind(this), options.parentView || this);
    this.dataSource.on("remove", this.removeRowById.bind(this), options.parentView || this);
    this.dataSource.on("reorder", this.reorder.bind(this), options.parentView || this);
    this.dataSource.on("updateAll", this.updateAll.bind(this), options.parentView || this);

    this.onGetView = function () {};
    this.getRowView = function () {};
};

ScrollTable.prototype = Object.create(cleverapps.EventEmitter.prototype);
ScrollTable.prototype.constructor = ScrollTable;

ScrollTable.prototype.getRows = function () {
    return this.rows;
};

ScrollTable.prototype.createRow = function (rowData) {
    return { ...rowData };
};

ScrollTable.prototype.updateAll = function (force) {
    if (this.showUpRunning) {
        return;
    }

    if (!this.onGetView() && !force) {
        return;
    }

    const newRows = this.dataSource.getData();
    const rows = this.rows.filter((row) => !row.removed);

    this.reorder();

    const toAdd = cleverapps.substract(newRows, rows, (r) => r.id);
    const toRemove = cleverapps.substract(rows, newRows, (r) => r.id);

    toRemove.forEach((row) => {
        this.removeRowById(row.id);
    });

    if (!toAdd.length) {
        return;
    }

    if (this.hasPendingRemoves()) {
        this.addAfterRemoves = true;
        return;
    }

    toAdd.forEach((row) => {
        this.addRow(row);
    });
};

ScrollTable.prototype.hasPendingRemoves = function () {
    return this.rows.some((row) => row.removed);
};

ScrollTable.prototype.stop = function () {
    this.trigger("stop");
};

ScrollTable.prototype.getPinnedRow = function () {
    return this.rows.find((r) => r.pinned);
};

ScrollTable.prototype.addRow = function (rowData) {
    if (this.showUpRunning) {
        return;
    }

    let index;
    for (index = 0; index < this.rows.length; index++) {
        if (rowData.weight >= this.rows[index].weight) {
            break;
        }
    }

    if (this._lastRemovedIndex !== -1) {
        const canInsertAtLastIndex = (!this.rows[this._lastRemovedIndex - 1] || this.rows[this._lastRemovedIndex - 1].weight >= rowData.weight)
            && (!this.rows[this._lastRemovedIndex] || this.rows[this._lastRemovedIndex].weight <= rowData.weight);

        if (canInsertAtLastIndex) {
            index = this._lastRemovedIndex;
            this._lastRemovedIndex = -1;
        } else if (index <= this._lastRemovedIndex) {
            this._lastRemovedIndex++;
        }
    }

    const existingRow = this.rows.find((r) => r.id === rowData.id);
    if (existingRow) {
        if (existingRow.removed) {
            this._removeRow(existingRow, true);
        } else {
            cleverapps.throwAsync(`[ScrollTable] Trying to add row with existing id: ${rowData.id}`);

            return;
        }
    }

    const newRow = this.createRow(rowData);
    this.rows.splice(index, 0, newRow);
    this.trigger("add", newRow, index);

    for (let i = 0; i < this.rows.length; i++) {
        this.trigger("move", this.rows[i].id, i);
    }
};

ScrollTable.prototype.removeRowById = function (rowId, silent) {
    if (this.showUpRunning) {
        return;
    }

    const row = this.rows.find((r) => !r.removed && r.id === rowId);

    if (row) {
        this._removeRow(row, silent);
    }
};

ScrollTable.prototype._removeRow = function (row, silent) {
    row.removed = true;

    this.trigger("toggleTouchScroll", false);

    const onRemoved = () => {
        const index = this.rows.indexOf(row);
        if (index !== -1) {
            this._lastRemovedIndex = index;
            this.rows.splice(index, 1);

            this.trigger("onRowRemoved");

            for (let i = 0; i < this.rows.length; i++) {
                this.trigger("move", this.rows[i].id, i);
            }

            if (!this.hasPendingRemoves()) {
                this.trigger("toggleTouchScroll", true);

                if (this.addAfterRemoves) {
                    this.addAfterRemoves = false;
                    this.updateAll(true);
                }
            }
        }
    };

    if (this.onGetView()) {
        this.trigger("remove", row.id, onRemoved, silent);
    } else {
        onRemoved();
    }
};

ScrollTable.prototype.updateRow = function (rowData) {
    const index = this.rows.findIndex((r) => !r.removed && r.id === rowData.id);

    this.rows[index] = this.createRow(rowData);

    this.trigger("updateRow", this.rows[index]);
};

ScrollTable.prototype.reorder = function () {
    if (this.showUpRunning) {
        return;
    }

    const newRows = this.dataSource.getData();

    const toUpdate = cleverapps.intersect(newRows, this.rows.filter((row) => !row.removed), (r) => r.id);

    toUpdate.forEach((rowData) => {
        this.updateRow(rowData);
    });

    this._sort();

    const pinnedRow = this.getPinnedRow();
    if (pinnedRow) {
        this.scrollToRow(pinnedRow.id);
    }

    this.trigger("reorder");
};

ScrollTable.prototype._sort = function () {
    this.rows.sort((a, b) => b.weight - a.weight);

    for (let i = 0; i < this.rows.length; i++) {
        this.trigger("move", this.rows[i].id, i);
    }
};

ScrollTable.prototype.scrollToRow = function (rowId, callback = () => {}) {
    if (!this.rows.find((r) => r.id === rowId)) {
        callback();
        return;
    }

    this.trigger("scrollToRow", rowId, callback);
};

ScrollTable.prototype.showUp = function () {
    const visibleRows = this.rows.filter((row) => this.getRowView(row.id).visible);

    this.showUpRunning = true;
    const callback = cleverapps.wait(visibleRows.length, () => {
        this.showUpRunning = false;

        this.updateAll();
    });

    for (let i = 0; i < visibleRows.length; i++) {
        this.trigger("showUp", visibleRows[i].id, i, callback);
    }
};

ScrollTable.prototype.setDirection = function (direction) {
    this.direction = direction;
    const scrollView = this.onGetView();
    if (scrollView) {
        this.stop();

        const viewDirection = direction === cleverapps.UI.VERTICAL
            ? cleverapps.UI.ScrollView.DIR_VERTICAL : cleverapps.UI.ScrollView.DIR_HORIZONTAL;
        scrollView.setDirection(viewDirection);
    }
};

ScrollTable.prototype.getDirection = function () {
    return this.direction;
};

ScrollTable.prototype.destructor = function () {
    this.dataSource.destructor();
    runCleaners(this);
};